import { useEffect, useState, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { useMap } from 'react-map-gl/maplibre';
import { useLeaflet } from 'react-leaflet';
import ResizeObserver from 'resize-observer-polyfill';
import { useDebouncedCallback } from 'use-debounce';
import { setBounds } from '#/shared/slices/location';
import { clearZoomToLocation, setMapZoomLevel } from '#/map/slice';
import { BoundOptionsContext } from '#/map/providers/BoundOptionsProvider';
import useCurrentLocation from '#/shared/hooks/useCurrentLocation';
import ROUTES from '#/constants/routes';
import useNavigate from '#/shared/hooks/useNavigate';
import useGeolocation from '#/shared/hooks/useGeolocation';
import featureFlags from '#/shared/feature-flags';
import mapConfig from '#/map-config';
import { zoomTo } from '#/map/utils/maplibre';
import { useAppSelector } from '#/state/hooks';

const { mapLibre } = featureFlags;

function MapViewManager(): null {
  const dispatch = useDispatch();
  const { map: leafletMap } = useLeaflet();
  const { current: mapLibreMap } = useMap();
  const map = mapLibre ? mapLibreMap : leafletMap;
  const { matchPath } = useNavigate();
  const { geolocationApproved } = useGeolocation();
  const { mapLimits, minZoom } = mapConfig;
  const options = useContext(BoundOptionsContext);
  const { currentLocation, isManualLocation } = useCurrentLocation();
  const isHomePage = matchPath([ROUTES.home]);
  const [mapTouched, setMapTouched] = useState(false);
  const [currentLocInitialized, setCurrentLocInitialized] = useState(false);
  const [lastLocation, setLastLocation] = useState(null);
  const [newGeolocationSession, setNewGeolocationSession] = useState(false);
  const [lastGeolocationApproved, setLastGeolocationApproved] =
    useState(geolocationApproved);

  const onMapMoveEnd = useDebouncedCallback(e => {
    // Batch the dispatch calls together to avoid potential race conditions
    const updates = {
      bounds: mapLibre
        ? map.getBounds().toArray()
        : e.target.getBounds().toBBoxString(),
      zoom: mapLibre ? map.getZoom() : e.target.getZoom(),
    };

    // Dispatch both updates together
    dispatch(setBounds(updates.bounds));
    dispatch(setMapZoomLevel(updates.zoom));
  }, 300);

  useEffect(() => {
    map.on('moveend', onMapMoveEnd);
    return () => map.off('moveend', onMapMoveEnd);
  }, [onMapMoveEnd, map]);

  // map config and setup
  useEffect(() => {
    const { top, bottom, left, right } = mapLimits;
    if (!mapLibre) {
      map.setMaxBounds([
        [top, left],
        [bottom, right],
      ]);

      map.setMinZoom(minZoom);

      // due to the app's adaptive design the map is sometimes resized during
      // app load and `invalidateSize` needs be invoked for Leaflet to adjust:
      // https://stackoverflow.com/a/36257493/2167004
      map.invalidateSize();
    }
  }, [map, mapLimits, minZoom]);

  useEffect(() => {
    if (!mapLibre) {
      // when the map element is resized update the leaflet map object so that
      // updates its state to reflect the change to the visible area
      // https://stackoverflow.com/a/63319084/2167004

      // the otp-ui wrapper on the react-leaflet map doesn't provide a way
      // to give the map div an `id` so a class name is used to fetch it
      const getLeafletContainerElement = (): Element | undefined =>
        document.getElementsByClassName('leaflet-container')[0];

      const mapResizeObserver = new ResizeObserver(() => {
        if (getLeafletContainerElement())
          map.invalidateSize({
            // passing this option ensures that map overlays get redrawn
            // after the resize
            debounceMoveend: true,
          });
      });

      const leafletContainerElement = getLeafletContainerElement();
      if (leafletContainerElement) {
        mapResizeObserver.observe(leafletContainerElement);
      }

      return () => {
        mapResizeObserver.disconnect();
      };
    }

    return () => {
      // Must return a function to satisfy consistent-return
    };
  }, [map]);

  useEffect(() => {
    map.once('mousedown', () => {
      setMapTouched(true);
    });
  }, [map]);

  useEffect(() => {
    if (isHomePage) {
      if (
        currentLocation &&
        (!currentLocInitialized ||
          newGeolocationSession ||
          (isManualLocation && currentLocation !== lastLocation))
      ) {
        zoomTo(map, currentLocation, mapLibre ? {} : options);
      } else if (!currentLocation && !mapTouched && !geolocationApproved) {
        // TODO: the get/set zoom is a hack that triggers a vehicle layer render (on mobile)
        //       without this zoom event, the vehicles will not render on smaller/resized windows.
        //       todo - look at OTP-UI / zoom based markers for reason why setZoom is needed to render
        const zoom = map.getZoom();
        map.setZoom(zoom);
      }
    }

    // current location isn't always available immediately after geolocation is
    // approved so the session is considered new until first render that has it
    if (newGeolocationSession && currentLocation) {
      setNewGeolocationSession(false);
    } else if (geolocationApproved !== lastGeolocationApproved) {
      setNewGeolocationSession(geolocationApproved);
    }

    setLastGeolocationApproved(geolocationApproved);
    setLastLocation(currentLocation);

    if (currentLocation && !currentLocInitialized) {
      setCurrentLocInitialized(true);
    }
  }, [
    dispatch,
    isHomePage,
    options,
    currentLocation,
    map,
    currentLocInitialized,
    mapTouched,
    geolocationApproved,
    lastLocation,
    isManualLocation,
    lastGeolocationApproved,
    newGeolocationSession,
  ]);

  const { hideMapOnMobile } = useAppSelector(s => s.map);

  useEffect(() => {
    if (!hideMapOnMobile) {
      if (mapLibre) {
        map.resize();
      } else {
        map.invalidateSize();
      }
    }
  }, [hideMapOnMobile, map]);

  const { zoomToLocation } = useAppSelector(s => s.map);
  useEffect(() => {
    if (zoomToLocation) {
      zoomTo(map, zoomToLocation, mapLibre ? {} : options);
      dispatch(clearZoomToLocation());
    }
  }, [map, dispatch, options, zoomToLocation]);

  return null;
}

export default MapViewManager;
